import { useRef, useEffect, useState } from 'react';
import Chart from 'chart.js/auto';

const CRIMSON = '#780000';
const GOLD = '#b8862a';
const FONT = "'Manrope', sans-serif";

const TrendsChart = ({ data, loading }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [chartError, setChartError] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0 || loading) return;

    try {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');

      const labels = data.map(d => {
        const date = new Date(d.date);
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
      });

      const streamsData = data.map(d => d.streams);
      const salesData = data.map(d => d.sales);

      chartInstanceRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Streams',
              data: streamsData,
              borderColor: CRIMSON,
              backgroundColor: 'rgba(120, 0, 0, 0.10)',
              fill: true,
              tension: 0.45,
              borderWidth: 3,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointBackgroundColor: CRIMSON,
              yAxisID: 'y'
            },
            {
              label: 'Ventes',
              data: salesData,
              borderColor: GOLD,
              backgroundColor: 'rgba(184, 134, 42, 0.10)',
              fill: true,
              tension: 0.45,
              borderWidth: 2.5,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointBackgroundColor: GOLD,
              yAxisID: 'y1'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(23, 23, 25, 0.92)',
              padding: 12,
              cornerRadius: 10,
              titleFont: { size: 13, family: FONT },
              bodyFont: { size: 12, family: FONT }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                maxTicksLimit: 8,
                font: { size: 11, family: FONT },
                color: '#9b958f'
              }
            },
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              grid: { color: 'rgba(23, 23, 25, 0.06)' },
              ticks: {
                maxTicksLimit: 5,
                font: { size: 11, family: FONT },
                color: '#9b958f'
              }
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              grid: { drawOnChartArea: false },
              ticks: {
                maxTicksLimit: 5,
                font: { size: 11, family: FONT },
                color: '#9b958f'
              }
            }
          }
        }
      });
    } catch (err) {
      console.error('Chart error:', err);
      setChartError(err.message);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [data, loading]);

  if (loading || !data || data.length === 0) {
    return (
      <>
        <div className="chart-head">
          <div><span className="content-kicker">Tendance</span><h3>Écoutes & ventes</h3></div>
        </div>
        <div className="dna-canvas is-empty">
          <span>{loading ? 'Chargement du graphique…' : 'Pas encore de données sur cette période.'}</span>
        </div>
      </>
    );
  }

  if (chartError) {
    return (
      <>
        <div className="chart-head">
          <div><span className="content-kicker">Tendance</span><h3>Écoutes & ventes</h3></div>
        </div>
        <div className="dna-canvas is-empty">
          <span>Erreur de chargement du graphique</span>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="chart-head">
        <div><span className="content-kicker">Tendance</span><h3>Écoutes & ventes</h3></div>
        <span className="chart-legend"><i /> Streams <i /> Ventes</span>
      </div>
      <div className="dna-canvas">
        <canvas ref={chartRef} />
      </div>
    </>
  );
};

export default TrendsChart;
